/**
 * Fases del ciclo mensual SSR + reglas para mapear día → fase → atajos.
 *
 * Es la "capa por momento del mes" del dashboard contextual: no depende
 * del usuario sino del estado típico del proceso administrativo del
 * comité a lo largo del mes. La fase determina qué CTAs se priorizan
 * en la pantalla de inicio.
 *
 * Nota: las reglas se eligen por experiencia operativa promedio en SSR;
 * cada comité puede tener su propio calendario (ver `Sector.dia_lectura`
 * y `dia_facturacion`). Cuando tengamos KPIs de datos reales podremos
 * cruzar la fase con el estado real (ej. "es día 8 pero las lecturas
 * todavía están al 30%, prioriza lecturas").
 */

export type CicloFase =
  | "lecturas"
  | "cierre"
  | "emision"
  | "cobranza"
  | "morosidad";

export interface CicloFaseInfo {
  id: CicloFase;
  titulo: string;
  descripcion: string;
  /** Color del card destacado en el dashboard. */
  tono: "blue" | "amber" | "slate" | "emerald" | "rose";
  /** Día del mes (1-31) en que arranca la fase. */
  diasInicio: number;
  /**
   * Día del mes (1-31) en que termina la fase. Puede ser MENOR que
   * `diasInicio` cuando la fase cruza el fin de mes (ej. emisión 30 → 1).
   */
  diasFin: number;
  /**
   * Largo del arco de la fase en días (1-31). El ciclo es circular, así
   * que la suma de los `span` de las 5 fases es siempre 31. Se usa para
   * el ancho de los segmentos del timeline y el progreso dentro de la fase.
   */
  span: number;
}

/** Reloj mensual canónico usado para el modelo cíclico (ver DEFAULT_CONFIG). */
const RELOJ = 31;

/** Distancia hacia adelante entre dos días del mes en el reloj circular. */
function distanciaCiclica(desde: number, hasta: number): number {
  const d = (hasta - desde + RELOJ) % RELOJ;
  return d === 0 ? RELOJ : d;
}

/** Configuración de días por empresa (viene del endpoint del backend). */
export interface CicloConfigDias {
  dia_lecturas_inicio: number;
  dia_cierre_inicio: number;
  dia_emision_inicio: number;
  dia_cobranza_inicio: number;
  dia_morosidad_inicio: number;
}

const DEFAULT_CONFIG: CicloConfigDias = {
  dia_lecturas_inicio: 1,
  dia_cierre_inicio: 6,
  dia_emision_inicio: 11,
  dia_cobranza_inicio: 16,
  dia_morosidad_inicio: 25,
};

const META: ReadonlyArray<{
  id: CicloFase;
  titulo: string;
  descripcion: string;
  tono: CicloFaseInfo["tono"];
  inicioKey: keyof CicloConfigDias;
}> = [
  {
    id: "lecturas",
    titulo: "Toma de lecturas",
    descripcion:
      "Inicio del ciclo: registrar las lecturas mensuales de los medidores y completar el padrón.",
    tono: "blue",
    inicioKey: "dia_lecturas_inicio",
  },
  {
    id: "cierre",
    titulo: "Cierre del periodo",
    descripcion:
      "Validar lecturas tomadas, prevalidar el cierre, calcular consumos y subsidios, dejar el cierre listo para emitir.",
    tono: "slate",
    inicioKey: "dia_cierre_inicio",
  },
  {
    id: "emision",
    titulo: "Emisión de boletas",
    descripcion:
      "Generar las boletas del ciclo y entregar la proforma del cierre. Comunicar reajustes si los hubiera.",
    tono: "emerald",
    inicioKey: "dia_emision_inicio",
  },
  {
    id: "cobranza",
    titulo: "Cobranza",
    descripcion:
      "Registrar pagos en caja y dar seguimiento a las cuentas con saldo pendiente.",
    tono: "amber",
    inicioKey: "dia_cobranza_inicio",
  },
  {
    id: "morosidad",
    titulo: "Morosidad y cortes",
    descripcion:
      "Emitir órdenes de corte para los socios morosos y dar seguimiento al cierre administrativo del mes.",
    tono: "rose",
    inicioKey: "dia_morosidad_inicio",
  },
];

/**
 * Construye los arcos de cada fase a partir de la config del comité.
 *
 * Modelo **circular**: cada fase ocupa el arco desde su día de inicio hasta
 * el inicio de la fase siguiente (la última cierra contra la primera). El
 * ciclo puede cruzar el fin de mes, así que `diasFin` puede ser menor que
 * `diasInicio` (ej. emisión 30 → 31 → cobranza 1). La suma de los `span`
 * siempre da 31.
 */
export function buildFases(
  config: CicloConfigDias = DEFAULT_CONFIG,
): CicloFaseInfo[] {
  return META.map((m, idx) => {
    const inicio = config[m.inicioKey];
    // La fase siguiente en el ciclo (la última cierra contra la primera).
    const proximaKey = META[(idx + 1) % META.length]!.inicioKey;
    const span = distanciaCiclica(inicio, config[proximaKey]);
    const fin = ((inicio - 1 + span - 1) % RELOJ) + 1;
    return {
      id: m.id,
      titulo: m.titulo,
      descripcion: m.descripcion,
      tono: m.tono,
      diasInicio: inicio,
      diasFin: fin,
      span,
    };
  });
}

export function faseDelDia(
  fecha: Date = new Date(),
  config?: CicloConfigDias,
): CicloFaseInfo {
  const fases = buildFases(config);
  const dia = fecha.getDate();
  // La fase actual es aquella cuyo inicio es el más cercano "hacia atrás"
  // en el reloj circular: minimiza la distancia inicio → día. Así cada día
  // del mes cae en exactamente un arco, aunque el ciclo cruce el fin de mes.
  let elegida = fases[0]!;
  let mejor = Number.POSITIVE_INFINITY;
  for (const f of fases) {
    const d = (dia - f.diasInicio + RELOJ) % RELOJ; // 0..30 hacia adelante
    if (d < mejor) {
      mejor = d;
      elegida = f;
    }
  }
  return elegida;
}

/** Lista de fases derivada de la config (mismo orden que META). */
export function todasLasFases(
  config?: CicloConfigDias,
): readonly CicloFaseInfo[] {
  return buildFases(config);
}

const NOMBRES_MES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function nombrePeriodo(fecha: Date = new Date()): string {
  const mes = NOMBRES_MES[fecha.getMonth()];
  return `${mes} ${fecha.getFullYear()}`;
}

const NOMBRES_DIA = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
];

export function fechaLarga(fecha: Date = new Date()): string {
  // Solo la primera letra en mayúscula ("Domingo 12 de julio"). No usar
  // el `capitalize` de CSS en el render: capitaliza CADA palabra y produce
  // el incorrecto "Domingo 12 De Julio".
  const dia = NOMBRES_DIA[fecha.getDay()] ?? "";
  const diaCap = dia.charAt(0).toUpperCase() + dia.slice(1);
  return `${diaCap} ${fecha.getDate()} de ${NOMBRES_MES[fecha.getMonth()]}`;
}
